import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ClinicalSheetRowInterpretation, ClinicalSubmissionDetail } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import {
  caseRoleLabel,
  confidenceLabel,
  occupancyLabel,
  reviewReasonLabel,
  rowFlagLabels,
  rowReviewStatusLabel,
  rowReviewStatusMarker,
  rowReviewStatusVariant,
  signatureLabel,
  sourceCrossOutLabel,
  studentRowNeedsAction,
  submissionStatusLabel,
  submissionStatusVariant,
} from '../../lib/submissionWorkflowLabels.js';

function RowEditor({
  row,
  editable,
  onSave,
  saving,
}: {
  row: ClinicalSheetRowInterpretation;
  editable: boolean;
  onSave: (patch: {
    procedureText?: string;
    caseDate?: string;
    specialty?: string;
    caseRole?: NonNullable<ClinicalSheetRowInterpretation['caseRole']>;
    studentOccupancyResolution?: ClinicalSheetRowInterpretation['studentOccupancyResolution'];
    studentSignatureResolution?: ClinicalSheetRowInterpretation['studentSignatureResolution'];
    studentCrossedOutAcknowledged?: boolean;
    studentCrossedOutResolution?: ClinicalSheetRowInterpretation['studentCrossedOutResolution'];
    confirmRow?: boolean;
  }) => Promise<void>;
  saving: boolean;
}) {
  const [procedureText, setProcedureText] = useState(
    row.procedureTextNormalized ?? row.automatedProcedureNormalized ?? '',
  );
  const [caseDate, setCaseDate] = useState(row.caseDateNormalized ?? row.automatedCaseDateNormalized ?? '');
  const [specialty, setSpecialty] = useState(row.specialtyNormalized ?? row.automatedSpecialtyNormalized ?? '');
  const [caseRole, setCaseRole] = useState(row.caseRole ?? row.automatedCaseRole ?? 'FIRST_SCRUB');
  const [studentOccupancyResolution, setStudentOccupancyResolution] = useState<
    ClinicalSheetRowInterpretation['studentOccupancyResolution'] | ''
  >(row.studentOccupancyResolution ?? '');
  const [studentSignatureResolution, setStudentSignatureResolution] = useState<
    ClinicalSheetRowInterpretation['studentSignatureResolution'] | ''
  >(row.studentSignatureResolution ?? '');
  const [studentCrossedOutResolution, setStudentCrossedOutResolution] = useState<
    NonNullable<ClinicalSheetRowInterpretation['studentCrossedOutResolution']> | ''
  >(row.studentCrossedOutResolution ?? '');
  const flags = rowFlagLabels(row);
  const needsAction = studentRowNeedsAction(row);

  return (
    <div
      className={`review-row ${needsAction ? 'review-row--attention' : ''} ${
        row.reviewStatus === 'INSTRUCTOR_REVIEW_REQUIRED' ? 'review-row--instructor' : ''
      }`}
      data-review-status={row.reviewStatus}
    >
      <div className="review-row__header">
        <div>
          <span className="review-row__marker" aria-hidden>
            {rowReviewStatusMarker(row.reviewStatus)}
          </span>
          <strong>Row {row.rowIndex}</strong>
        </div>
        <StatusBadge
          status={rowReviewStatusLabel(row.reviewStatus)}
          variant={rowReviewStatusVariant(row.reviewStatus)}
        />
      </div>
      {needsAction && (
        <p className="review-row__action-cue">Action required on this row before you can submit.</p>
      )}
      <div className="review-row__meta detail-list detail-list--inline text-sm">
        <span>
          <strong>Date:</strong> {row.caseDateNormalized ?? row.automatedCaseDateNormalized ?? '—'}
        </span>
        <span>
          <strong>Specialty:</strong> {row.specialtyNormalized ?? row.automatedSpecialtyNormalized ?? '—'}
        </span>
        <span>
          <strong>Procedure:</strong>{' '}
          {row.procedureTextNormalized ?? row.automatedProcedureNormalized ?? '—'}
        </span>
        <span>
          <strong>Role:</strong> {caseRoleLabel(row.caseRole ?? row.automatedCaseRole)}
        </span>
        <span>
          <strong>Confidence:</strong> {confidenceLabel(row.confidence ?? null)}
        </span>
      </div>
      {row.reviewReasons.length > 0 && (
        <div className="review-row__section">
          <h4 className="review-row__section-title">Review reasons</h4>
          <ul className="review-row__reasons text-sm">
            {row.reviewReasons.map((code) => (
              <li key={code}>{reviewReasonLabel(code)}</li>
            ))}
          </ul>
        </div>
      )}
      {flags.length > 0 && (
        <div className="review-row__flags">
          {flags.map((flag) => (
            <span key={flag} className="badge badge--warning">
              {flag}
            </span>
          ))}
        </div>
      )}
      <div className="review-row__section">
        <h4 className="review-row__section-title">Sheet Notes</h4>
        <p className="text-muted text-small">
          {occupancyLabel(row.sourceOccupancyState)} ? {signatureLabel(row.sourceSignaturePresence)} ? Cross-out:{' '}
          {sourceCrossOutLabel(row.sourceCrossedOutState)}
        </p>
      </div>
      <div className="review-row__grid">
        <label>
          Procedure
          <input value={procedureText} disabled={!editable} onChange={(e) => setProcedureText(e.target.value)} />
        </label>
        <label>
          Case Date
          <input type="date" value={caseDate} disabled={!editable} onChange={(e) => setCaseDate(e.target.value)} />
        </label>
        <label>
          Specialty
          <input value={specialty} disabled={!editable} onChange={(e) => setSpecialty(e.target.value)} />
        </label>
        <label>
          Role
          <select
            value={caseRole}
            disabled={!editable}
            onChange={(e) => setCaseRole(e.target.value as typeof caseRole)}
          >
            <option value="FIRST_SCRUB">First Scrub</option>
            <option value="SECOND_SCRUB">Second Scrub</option>
            <option value="OBSERVATION">Observation</option>
          </select>
        </label>
        <label>
          Confirm Row Has Case / Row Is Blank
          <select
            value={studentOccupancyResolution ?? ''}
            disabled={!editable}
            onChange={(e) =>
              setStudentOccupancyResolution(
                e.target.value as ClinicalSheetRowInterpretation['studentOccupancyResolution'] | '',
              )
            }
          >
            <option value="">Not resolved</option>
            <option value="OCCUPIED">Confirm row has case</option>
            <option value="EMPTY">Confirm row is blank</option>
            <option value="UNCERTAIN">Still uncertain</option>
          </select>
        </label>
        <label>
          Confirm Signature
          <select
            value={studentSignatureResolution ?? ''}
            disabled={!editable}
            onChange={(e) =>
              setStudentSignatureResolution(
                e.target.value as ClinicalSheetRowInterpretation['studentSignatureResolution'] | '',
              )
            }
          >
            <option value="">Not resolved</option>
            <option value="PRESENT">Signature present</option>
            <option value="MISSING">Signature missing</option>
            <option value="UNCERTAIN">Signature unclear</option>
          </select>
        </label>
        {row.sourceCrossedOutState === 'UNCERTAIN' && editable && (
          <label>
            Cross-out on sheet
            <select
              value={studentCrossedOutResolution}
              disabled={!editable}
              onChange={(e) =>
                setStudentCrossedOutResolution(
                  e.target.value as NonNullable<ClinicalSheetRowInterpretation['studentCrossedOutResolution']> | '',
                )
              }
            >
              <option value="">Needs review</option>
              <option value="PRESENT">Crossed out</option>
              <option value="ABSENT">Not crossed out</option>
            </select>
          </label>
        )}
      </div>
      {row.sourceCrossedOutState === 'PRESENT' && editable && (
        <label className="review-row__checkbox">
          <input
            type="checkbox"
            checked={row.studentCrossedOutAcknowledged}
            onChange={(e) =>
              void onSave({ studentCrossedOutAcknowledged: e.target.checked, procedureText, caseDate, specialty, caseRole })
            }
          />
          I acknowledge the crossed-out concern on this row
        </label>
      )}
      {editable && (
        <div className="review-row__actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={() =>
              void onSave({
                procedureText,
                caseDate: caseDate || undefined,
                specialty,
                caseRole,
                studentOccupancyResolution: studentOccupancyResolution
                  ? studentOccupancyResolution
                  : undefined,
                studentSignatureResolution: studentSignatureResolution
                  ? studentSignatureResolution
                  : undefined,
                studentCrossedOutResolution: studentCrossedOutResolution
                  ? studentCrossedOutResolution
                  : undefined,
              })
            }
          >
            {saving ? 'Saving?' : 'Save Row'}
          </button>
          {row.sourceOccupancyState === 'UNCERTAIN' && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={saving}
              onClick={() =>
                void onSave({
                  procedureText,
                  caseDate: caseDate || undefined,
                  specialty,
                  caseRole,
                  studentOccupancyResolution: 'OCCUPIED',
                  studentSignatureResolution: studentSignatureResolution
                    ? studentSignatureResolution
                    : undefined,
                  confirmRow: true,
                })
              }
            >
              Confirm Row Has Case
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function StudentSubmissionReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<ClinicalSubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    if (!id) return Promise.resolve();
    setError(null);
    return api
      .getWorkflowSubmission(id)
      .then((res) => setSubmission(res.submission))
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setError(
            err.status === 403
              ? 'You do not have access to this submission.'
              : 'Submission not found.',
          );
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load submission');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const editable =
    submission?.status === 'STUDENT_REVIEW_REQUIRED' || submission?.status === 'CLARIFICATION_REQUESTED';

  const handleSaveRow = async (
    rowId: string,
    patch: Parameters<typeof RowEditor>[0]['onSave'] extends (p: infer P) => unknown ? P : never,
  ) => {
    if (!id) return;
    setSavingRowId(rowId);
    setActionError(null);
    try {
      const res = await api.updateWorkflowRow(id, rowId, patch);
      setSubmission(res.submission);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save row');
    } finally {
      setSavingRowId(null);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await api.submitWorkflowSubmission(id);
      navigate('/student/sheets');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not submit for faculty review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState message="Loading submission…" />;
  if (error || !submission) {
    return (
      <div className="page">
        <ErrorBanner message={error ?? 'Submission not found'} onRetry={() => void load()} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Review Clinical Sheet"
        subtitle={`${submission.rows.length} case rows ? ${submission.reviewNeededCount} need attention`}
        actions={
          <Link to="/student/sheets" className="btn btn-secondary">
            Back to Sheets
          </Link>
        }
      />

      <div className="card review-summary">
        <StatusBadge
          status={submissionStatusLabel(submission.status)}
          variant={submissionStatusVariant(submission.status)}
        />
        {submission.returnNote && (
          <p className="banner banner-warning">Faculty note: {submission.returnNote}</p>
        )}
        {submission.sourceEvidence && (
          <div className="evidence-preview">
            <h3 className="card-title">Your Clinical Sheet</h3>
            <p className="text-muted text-small">
              {submission.sourceEvidence.originalFilename ?? 'Clinical sheet'}
            </p>
            {submission.sourceEvidence.detectedMimeType.startsWith('image/') ? (
              <img
                src={api.documentContentUrl(submission.sourceEvidence.id)}
                alt="Uploaded clinical sheet"
                className="evidence-preview__image"
              />
            ) : (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  window.open(api.documentContentUrl(submission.sourceEvidence!.id), '_blank', 'noopener,noreferrer')
                }
              >
                Open PDF
              </button>
            )}
          </div>
        )}
      </div>

      {actionError && <ErrorBanner message={actionError} />}

      {submission.rows.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<span aria-hidden>📋</span>}
            title="No case rows"
            description="This submission has no interpreted rows to review."
          />
        </div>
      ) : submission.reviewNeededCount === 0 && editable ? (
        <div className="banner banner--success">All rows are resolved — you can submit for faculty review.</div>
      ) : null}

      <div className="review-rows">
        {submission.rows.map((row) => (
          <RowEditor
            key={row.id}
            row={row}
            editable={editable}
            saving={savingRowId === row.id}
            onSave={(patch) => handleSaveRow(row.id, patch)}
          />
        ))}
      </div>

      {editable && (
        <div className="card review-actions">
          <p className="text-muted">
            Resolve all flagged rows, then submit for faculty review. Faculty approval is required before cases count
            toward your academic ledger.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-lg"
            disabled={submitting || submission.reviewNeededCount > 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Submitting?' : 'Submit for Faculty Review'}
          </button>
          {submission.reviewNeededCount > 0 && (
            <p className="text-muted text-small">{submission.reviewNeededCount} row(s) still need review.</p>
          )}
        </div>
      )}
    </div>
  );
}
